import { f as fetchConfig } from './index-DoPtEJTE.js';

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {};

      // Get options from element data and combine with this.options
      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      // Listen to form submit event
      if (this.dataset.noForm == 'false') {
        this.form = this.querySelector('form');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      } else {
        this.submitButton = this.querySelector('button[type="submit"]');
        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
      }

      // Listen to variant change events
      this.variants = this.querySelector('[data-variant-selector]');
      this.addEventListener('variant-change', this.onVariantChange.bind(this));
    }

    onVariantChange(evt) {
      this.currentVariant = evt.detail.variant;
      this.toggleAddButton();

      this.updateMedia();
      this.updateURL();
      this.renderProductInfo();
    }

    updateMedia() {
      if (!this.currentVariant || !this.currentVariant?.featured_media) return;
      const newMedia = document.querySelector(
        `[data-media-id="${this.options.sectionId}-${this.currentVariant.featured_media.id}"]`
      );
      if (!newMedia) return;
      const parent = newMedia.parentElement;
      parent.prepend(newMedia);
      window.setTimeout(() => {
        parent.scroll(0, 0);
      });
    }

    // If this is a PDP main product form, update the url with the new variant
    updateURL() {
      if (!this.currentVariant || this.options.scope != 'main') return;

      // Update the url variant parameter or add it if it doesn't exist
      const url = new URL(document.location);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url);
    }

    // Get the product data from the main-product section
    renderProductInfo() {
      if (!this.options.sectionId) return;

      // Clean up existing previews before fetching new content
      this.cleanupPreviews();

      fetch(
        `/products/${this.options.handle}?variant=${this.currentVariant.id}&section=${this.options.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          // update price
          this.updateElement(html, `price-${this.options.sectionId}`);

          // update inventory_quantity
          this.updateElement(html, `inventory-${this.options.sectionId}`);

          // update bundle properties
          this.updateElement(html, `bundle-properties-${this.options.sectionId}`);

          // update bundle form
          this.updateElement(html, `product-bundle-${this.options.sectionId}`);
        });
    }

    // Clean up bundle and engraving previews when variant changes
    cleanupPreviews() {
      // Find and cleanup all previews via coordinator
      const productMediaSlider = document.querySelector('product-media-slider');
      if (productMediaSlider && productMediaSlider.previewCoordinator) {
        productMediaSlider.previewCoordinator.clearAllPreviews();
      }

      // Reset engraving checkbox state
      const engravingComponent = document.querySelector('product-engraving');
      if (engravingComponent && engravingComponent.enableCheckbox) {
        engravingComponent.enableCheckbox.checked = false;
        // Trigger change event to update component state
        engravingComponent.enableCheckbox.dispatchEvent(new Event('change'));
      }
    }

    // Replace any element based on the ID with the new html
    updateElement(html, selector) {
      const source = html.getElementById(selector);
      const destination = document.getElementById(selector);

      if (source && destination) {
        if (destination.tagName === 'INPUT') {
          destination.value = source.value;
        } else if (selector.includes('product-bundle-')) {
          // For bundle forms, we need to handle the custom element properly
          this.updateBundleElement(source, destination);
        } else if (selector.includes('product-engraving-')) {
          // For engraving forms, we need to handle the custom element properly
          this.updateEngravingElement(source, destination);
        } else {
          destination.innerHTML = source.innerHTML;
        }
      }
    }

    // Update bundle element - replace entirely and reinitialize
    updateBundleElement(source, destination) {
      // Check if the destination element itself is the product-bundle element
      destination.tagName === 'PRODUCT-BUNDLE'
          ? destination
          : destination.querySelector('product-bundle');

      // Replace the entire content to get fresh bundle options for the new variant
      destination.innerHTML = source.innerHTML;

      // Check if the updated element is the product-bundle element
      const newBundle =
        destination.tagName === 'PRODUCT-BUNDLE'
          ? destination
          : destination.querySelector('product-bundle');

      // Force reinitialization of the product-bundle element
      if (newBundle && newBundle.tagName === 'PRODUCT-BUNDLE') {
        // Reset the initialized flag and call connectedCallback manually
        newBundle.initialized = false;
        if (newBundle.connectedCallback) {
          newBundle.connectedCallback();
        }
      }
    }

    // Update engraving element - replace entirely and reinitialize
    updateEngravingElement(source, destination) {
      // Check if the destination element itself is the product-engraving element
      destination.tagName === 'PRODUCT-ENGRAVING'
          ? destination
          : destination.querySelector('product-engraving');

      // Replace the entire content to get fresh engraving options for the new variant
      destination.innerHTML = source.innerHTML;

      // Check if the updated element is the product-engraving element
      const newEngraving =
        destination.tagName === 'PRODUCT-ENGRAVING'
          ? destination
          : destination.querySelector('product-engraving');

      // Force reinitialization of the product-engraving element
      if (newEngraving && newEngraving.tagName === 'PRODUCT-ENGRAVING') {
        // Call init method manually to reinitialize the component
        if (newEngraving.init) {
          newEngraving.init();
        }
      }
    }

    /**
     * toggleAddButton
     * @param {Boolean} available
     * @returns {void}
     */
    toggleAddButton() {
      this.addButton = this.addButton || this.querySelector('[name="add"]');
      this.addButtonText =
        this.addButtonText || this.addButton.querySelector('[data-add-to-cart-button-text]');

      // Dis/enable the button
      if (!this.addButton) return;
      this.addButton.disabled = !this.currentVariant.available;

      // Toggle text
      if (!this.addButtonText) return;
      if (!this.currentVariant.available) {
        this.addButtonText.textContent = window.variantStrings.soldOut;
      } else {
        this.addButtonText.textContent = window.variantStrings.addToCart;
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();

      this.submitButton = this.submitButton || this.querySelector('[type="submit"]');
      this.submitButton.setAttribute('disabled', true);
      this.submitButton.classList.add('button--loading');

      // Get bundle data
      const bundleComponent = document.querySelector('product-bundle');
      const bundleData = bundleComponent ? bundleComponent.getBundleCartData() : null;

      // Get engraving data
      const engravingComponent = document.querySelector('product-engraving');
      const engravingData = engravingComponent ? engravingComponent.getEngravingCartData() : null;

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Content-Type'] = 'application/json';

      this.cartNotification = this.cartNotification || document.querySelector('cart-notification');
      this.cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');

      this.cartItems = document.querySelector('cart-items');

      // Get sections to fetch
      let sectionsToFetch = [];
      if (this.cartNotification) {
        [
          ...this.cartNotification.getSectionsToRender().map((section) => section.section),
        ];
      }

      if (this.cartItems) {
        sectionsToFetch = [
          ...sectionsToFetch,
          ...this.cartItems.getSectionsToRender().map((section) => section.section),
        ];
        // return unique values of array
        sectionsToFetch = [...new Set(sectionsToFetch)];
      }

      const cartItems = [];

      // bundle products
      if (bundleData) {
        bundleData.forEach((option) => {
          cartItems.push({
            id: option.id,
            quantity: parseInt(this.querySelector('[name="quantity"]').value),
            properties: {
              _component: option.properties._component,
            },
          });
        });
      }

      // engraving product
      if (engravingData && engravingData.hasEngraving) {
        cartItems.push({
          id: engravingData.engravingProduct.id,
          quantity: parseInt(this.querySelector('[name="quantity"]').value),
          properties: {
            _engraving: engravingData.engravingProduct.properties._engraving,
          },
        });
      }

      // Add the current product
      let currentProduct = {
        id: parseInt(this.querySelector('[name="id"]').value),
        quantity: parseInt(this.querySelector('[name="quantity"]').value),
        properties: {},
      };

      // Add the current product id to the properties
      if ((bundleData && bundleData.length > 0) || (engravingData && engravingData.hasEngraving)) {
        currentProduct.properties._configuration = parseInt(
          this.querySelector('[name="id"]').value
        );
      }

      // Add bundle properties if bundle data exists
      if (bundleData && bundleData.length > 0) {
        // Add each bundle option as a separate property
        bundleData.forEach((option, index) => {
          currentProduct.properties[`_bundle_option_${index + 1}`] =
            option.properties._bundle_option_name;
        });
      }

      // Add engraving properties to main product if engraving data exists
      if (engravingData && engravingData.hasEngraving) {
        // Add engraving details as properties on main product for cart display
        if (engravingData.engravingProduct.properties._engraving_text) {
          currentProduct.properties._engraving_text =
            engravingData.engravingProduct.properties._engraving_text;
        }
        if (engravingData.engravingProduct.properties._engraving_icon) {
          currentProduct.properties._engraving_icon =
            engravingData.engravingProduct.properties._engraving_icon;
        }
        if (engravingData.engravingProduct.properties._engraving_font) {
          currentProduct.properties._engraving_font =
            engravingData.engravingProduct.properties._engraving_font;
        }
      }

      cartItems.push(currentProduct);

      const formData = {
        items: cartItems,
        sections_url: window.location.pathname,
        ...(sectionsToFetch.length > 0 && { sections: sectionsToFetch }),
      };

      config.body = JSON.stringify(formData);

      // Get the current variant data from the variant component
      this.currentVariant = this.querySelector('[data-variant-selector]').getCurrentVariantData();

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((parsedState) => {
          // When a quantity error is returned
          if (parsedState.status == 422) {
            this.onCartError(parsedState, sectionsToFetch);
            return;
          } else {
            this.onCartSuccess(parsedState);
          }
          // Trigger cart updated event with the latest cart data and sections to render
          this.dispatchEvent(
            new CustomEvent('cart-updated', {
              bubbles: true,
              detail: {
                ...parsedState,
              },
            })
          );
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('button--loading');
          this.submitButton.removeAttribute('disabled');
        });
    }

    // When item is added to the cart
    onCartSuccess(parsedState) {
      if (this.cartDrawer) {
        // Render the returned sections
        this.cartDrawer.renderContents(parsedState);
      }

      // Show the notification or open the drawer
      switch (this.submitButton.dataset.addToCartBehavior) {
        case 'open_cart_drawer':
          if (this.cartDrawer) {
            // Set timeout to force animation. Because content is just updated with renderContents
            setTimeout(() => {
              this.cartDrawer.open();
            });
          }
          break;
        case 'show_cart_notification':
          if (this.cartNotification) {
            this.cartNotification.setActiveElement(document.activeElement);
            this.cartNotification.renderContents(parsedState);
          }
      }
    }

    // Handle any AddToCart errors
    onCartError(parsedState, sectionsToFetch) {
      if (this.cartNotification) {
        this.cartNotification.setActiveElement(document.activeElement);
        this.cartNotification.renderCartError(parsedState.description);
      }

      if (this.cartDrawer) {
        // Let the cart drawer update itself to show all available products are added to the cart
        fetch('/?sections=' + sectionsToFetch.join())
          .then((response) => response.json())
          .then((parsedState) => {
            this.cartDrawer.renderContents({ sections: parsedState });
          });
      }
    }
  }

  window.ProductForm = ProductForm;
  customElements.define('product-form', ProductForm);
}
